export const validateField = (
  fieldName,
  value,
  validationRules,
  allFormData = {},
  t,
  isEditMode = false
) => {
  const rules = validationRules[fieldName];
  if (!rules) return null;

  const isRequired =
    typeof rules.required === 'function'
      ? rules.required(allFormData, null, isEditMode)
      : rules.required;

  if (
    isRequired &&
    (value === null || value === undefined || (typeof value === 'string' && value.trim() === ''))
  ) {
    return (
      (rules.requiredMessage && t ? t(rules.requiredMessage) : rules.requiredMessage) ||
      (t ? t('modals.form.validation.required') : 'Bu alan gerekli')
    );
  }
  if (rules.email && value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return (
        rules.emailMessage ||
        (t
          ? t('modals.form.validation.email')
          : 'Geçerli bir e-posta adresi girin')
      );
    }
  }
  if (rules.minLength && value && value.length < rules.minLength) {
    return (
      (rules.minLengthMessage && t ? t(rules.minLengthMessage) : rules.minLengthMessage) ||
      (t
        ? t('modals.form.validation.minLength', { min: rules.minLength })
        : `En az ${rules.minLength} karakter olmalı`)
    );
  }
  if (rules.maxLength && value && value.length > rules.maxLength) {
    return (
      rules.maxLengthMessage ||
      (t
        ? t('modals.form.validation.maxLength', { max: rules.maxLength })
        : `En fazla ${rules.maxLength} karakter olmalı`)
    );
  }
  if (rules.min && value && parseFloat(value) < rules.min) {
    return (
      rules.minMessage ||
      (t
        ? t('modals.form.validation.min', { min: rules.min })
        : `Minimum değer ${rules.min} olmalı`)
    );
  }
  if (rules.max && value && parseFloat(value) > rules.max) {
    return (
      rules.maxMessage ||
      (t
        ? t('modals.form.validation.max', { max: rules.max })
        : `Maksimum değer ${rules.max} olmalı`)
    );
  }
  if (rules.pattern && value && !rules.pattern.test(value)) {
    return (
      (typeof rules.patternMessage === 'function'
        ? rules.patternMessage(t)
        : rules.patternMessage) ||
      (t ? t('modals.form.validation.pattern') : 'Geçersiz format')
    );
  }
  if (rules.url && value) {
    try {
      new URL(value);
    } catch {
      return (
        rules.urlMessage ||
        (t ? t('modals.form.validation.url') : 'Geçerli bir URL girin')
      );
    }
  }
  if (rules.phone && value) {
    const phoneRegex = /^[+]?[0-9\s\-()]+$/;
    if (!phoneRegex.test(value)) {
      return (
        rules.phoneMessage ||
        (t
          ? t('modals.form.validation.phone')
          : 'Geçerli bir telefon numarası girin')
      );
    }
  }
  if (rules.file && value) {
    if (rules.file.maxSize && value.size > rules.file.maxSize) {
      return (
        rules.file.maxSizeMessage ||
        `Dosya boyutu ${rules.file.maxSize / 1024 / 1024}MB'dan küçük olmalı`
      );
    }
    if (
      rules.file.allowedTypes &&
      !rules.file.allowedTypes.includes(value.type)
    ) {
      return (
        rules.file.allowedTypesMessage ||
        `İzin verilen dosya türleri: ${rules.file.allowedTypes.join(', ')}`
      );
    }
  }
  if (rules.array && Array.isArray(value)) {
    if (rules.array.minLength && value.length < rules.array.minLength) {
      return (
        rules.array.minLengthMessage ||
        `En az ${rules.array.minLength} seçim yapın`
      );
    }
    if (rules.array.maxLength && value.length > rules.array.maxLength) {
      return (
        rules.array.maxLengthMessage ||
        `En fazla ${rules.array.maxLength} seçim yapın`
      );
    }
  }
  if (rules.date && value) {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return rules.dateMessage || 'Geçerli bir tarih girin';
    }

    if (rules.date.minDate) {
      const minDate = new Date(rules.date.minDate);
      if (date < minDate) {
        return (
          rules.date.minDateMessage ||
          `Tarih ${minDate.toLocaleDateString()} tarihinden sonra olmalı`
        );
      }
    }

    if (rules.date.maxDate) {
      const maxDate = new Date(rules.date.maxDate);
      if (date > maxDate) {
        return (
          rules.date.maxDateMessage ||
          `Tarih ${maxDate.toLocaleDateString()} tarihinden önce olmalı`
        );
      }
    }
  }
  if (rules.custom && typeof rules.custom === 'function') {
    const customResult = rules.custom(value, allFormData);
    if (typeof customResult === 'string' && t) {
      return t(customResult);
    }
    return customResult;
  }

  return null;
};

export const validateForm = (fields, formData, validationRules, t, isEditMode = false) => {
  const errors = {};

  fields.forEach(field => {
    const error = validateField(
      field.name,
      formData[field.name],
      validationRules,
      formData,
      t,
      isEditMode
    );
    if (error !== null && error !== undefined) {
      errors[field.name] = error;
    }
  });

  return errors;
};

export const transformFormData = (formData, transformations = {}) => {
  const transformed = { ...formData };

  Object.keys(transformations).forEach(fieldName => {
    const transformation = transformations[fieldName];
    if (typeof transformation === 'function' && fieldName in transformed) {
      transformed[fieldName] = transformation(transformed[fieldName]);
    }
  });

  return transformed;
};

export const serializeFormData = (formData, fileFields = []) => {
  const serialized = new FormData();

  Object.keys(formData).forEach(key => {
    const value = formData[key];

    if (fileFields.includes(key) && value) {
      if (value instanceof FileList) {
        Array.from(value).forEach(file => {
          serialized.append(key, file);
        });
      } else if (value instanceof File) {
        serialized.append(key, value);
      }
    } else if (Array.isArray(value)) {
      value.forEach(item => {
        serialized.append(`${key}[]`, item);
      });
    } else if (value !== null && value !== undefined) {
      serialized.append(key, value);
    }
  });

  return serialized;
};
